// ─────────────────────────────────────────────
//  PreviewBanner — always-visible bar shown while an admin is using the
//  "View as" testing mode. It sits in normal flow at the very top of the app
//  so it pushes the page down (never hides content) and makes it impossible to
//  forget you're impersonating. The "Exit" button drops back to the real admin.
// ─────────────────────────────────────────────
import React from "react";
import { useAuth } from "../context/AuthContext";
import { isNative } from "../native/platform";
import tokens from "../theme/tokens";

const { color } = tokens;

export default function PreviewBanner() {
  const { isPreviewing, accessLevelMeta, realUser, clearViewAs, previewName } = useAuth();
  if (!isPreviewing) return null;
  // On the native shell the mobile header owns identity and carries both the
  // "viewing as" signal and its Exit. This banner sits in normal flow at the
  // very top of the app, where the header's fixed bar would cover it — two
  // bars fighting over the same 60px, with the covered one holding the way
  // out. Same treatment MessagingLauncher already gets.
  if (isNative()) return null;

  // A specific-person preview (real user or test account) names that person and
  // their role; a generic tier preview names only the tier.
  const role = accessLevelMeta?.label || "another role";
  const viewingLabel = previewName ? `${previewName} (${role})` : role;

  return (
    <div style={s.bar} role="status" aria-live="polite">
      <span aria-hidden="true" style={{ fontSize: 16 }}>👁️</span>
      <span style={s.text}>
        Previewing as{" "}
        <strong>{viewingLabel}</strong>. Changes you
        make are saved as {realUser?.name || "the administrator"} (Administrator).
      </span>
      <button style={s.exit} onClick={clearViewAs}>
        ✕ Exit preview
      </button>
    </div>
  );
}

// Amber background (color.accent.closing) is the preview-mode identity signal —
// kept intentionally to make it unmissable. Dark text on amber exceeds 4.5:1.
const s = {
  bar: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    background: color.accent.closing,  // amber — preview identity
    color: color.text.primary,          // deep navy on amber: high contrast
    padding: "8px 16px",
    fontSize: 13,
    fontWeight: 600,
    fontFamily: "system-ui",
  },
  text: { flex: 1, lineHeight: 1.4 },
  exit: {
    background: color.text.primary,    // deep navy exit button
    color: color.accent.closing,        // amber text on navy: high contrast
    border: "none",
    borderRadius: 6,
    padding: "5px 12px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    flexShrink: 0,
  },
};
